import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const TMDB_IMG = 'https://image.tmdb.org/t/p/w500';

// Cinemark slug rules: lowercase → & → "and" → strip non-alphanum/space → trim → spaces → hyphens.
// & replacement MUST precede the strip step. Apostrophes, colons, hyphens, etc. are dropped.
// "You, Me & Tuscany" → "you-me-and-tuscany" | "Don't Move" → "dont-move"
function toCinemarkSlug(title) {
  return title.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '-');
}

function formatDate(d) {
  if (!d) return 'Release Date TBA';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

export default function MovieDetailSheet({
  movie,
  inWatchlist,
  onWatchlistToggle,
  isLoggedIn,
  isNowShowing,
  onClose,
}) {
  const [busy,     setBusy]     = useState(false);
  const [imgError, setImgError] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key closes
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const poster = movie.poster_path && !imgError
    ? `${TMDB_IMG}${movie.poster_path}`
    : null;

  let badge = null;
  if (movie.special_screening) {
    badge = { cls: 'badge-special', label: 'Special Screening' };
  } else if (isNowShowing) {
    badge = { cls: 'badge-theaters', label: 'Now Showing' };
  } else if (movie.tickets_on_sale) {
    badge = { cls: 'badge-on-sale', label: 'Tickets On Sale' };
  } else {
    badge = { cls: 'badge-coming', label: 'Coming Soon' };
  }

  async function handleAlert(e) {
    e.stopPropagation();
    if (busy || !onWatchlistToggle) return;
    setBusy(true);
    try { await onWatchlistToggle(movie.id, inWatchlist); }
    finally { setBusy(false); }
  }

  const cinemarkHref = movie.cinemark_url  || `https://www.cinemark.com/movies/${toCinemarkSlug(movie.title)}`;
  const ticketsAvailable = isNowShowing || movie.tickets_on_sale;

  const sheet = (
    <>
      {/* Backdrop */}
      <div className="movie-sheet-overlay" onClick={onClose} />

      <div className="movie-sheet" role="dialog" aria-modal="true" aria-label={movie.title}>

        {/* Mobile-only: sticky drag handle bar */}
        <div className="movie-sheet-topbar" aria-hidden="true">
          <div className="movie-sheet-handle" />
        </div>

        {/* Close button — always absolute top-right of the sheet */}
        <button className="movie-sheet-close" onClick={onClose} aria-label="Close" type="button">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Two-column inner layout: poster | content */}
        <div className="movie-sheet-inner">

          {/* Poster column */}
          <div className="movie-sheet-poster-col">
            {poster ? (
              <img
                className="movie-sheet-poster"
                src={poster}
                alt={movie.title}
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="movie-sheet-poster-placeholder" aria-hidden="true">🎬</div>
            )}
          </div>

          {/* Content column */}
          <div className="movie-sheet-body">
            {badge && (
              <span className={`card-badge movie-sheet-badge ${badge.cls}`}>
                {badge.label}
              </span>
            )}

            <h2 className="movie-sheet-title">{movie.title}</h2>
            <p className="movie-sheet-date">{formatDate(movie.release_date)}</p>

            {movie.overview ? (
              <p className="movie-sheet-overview">{movie.overview}</p>
            ) : (
              <p className="movie-sheet-overview movie-sheet-overview--empty">
                No description available yet.
              </p>
            )}

            <div className="movie-sheet-actions">
              {ticketsAvailable ? (
                <>
                  {movie.megaplex_url && (
                    <a
                      href={movie.megaplex_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary movie-sheet-btn"
                    >
                      🎟 Get Tickets — Megaplex
                    </a>
                  )}
                  <a
                    href={cinemarkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-blue movie-sheet-btn"
                  >
                    🎟 Get Tickets — Cinemark
                  </a>
                </>
              ) : isLoggedIn ? (
                <button
                  type="button"
                  className={`btn movie-sheet-btn${inWatchlist ? ' btn-ghost' : ' btn-primary'}`}
                  onClick={handleAlert}
                  disabled={busy}
                  onMouseEnter={() => setHovering(true)}
                  onMouseLeave={() => setHovering(false)}
                >
                  {busy
                    ? '…'
                    : inWatchlist
                    ? (hovering ? '✕ Remove Alert' : '✓ Alert Active')
                    : '🔔 Alert Me When Tickets Drop'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary movie-sheet-btn"
                  onClick={handleAlert}
                >
                  🔔 Alert Me When Tickets Drop
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(sheet, document.body);
}
